import { cn } from '@up2stay/up2promo-ui';
import type { ReactNode } from 'react';

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

type HighlightMatchTextProps = {
  text: string;
  query?: string;
  className?: string;
  markClassName?: string;
};

/**
 * Renders `text` with case-insensitive substring matches of `query`
 * wrapped in `<mark>` so search results show why they matched.
 */
export function HighlightMatchText({
  text,
  query,
  className,
  markClassName,
}: HighlightMatchTextProps): ReactNode {
  const q = query?.trim();
  if (!q || !text) {
    return className ? <span className={className}>{text}</span> : text;
  }

  const pattern = new RegExp(`(${escapeRegExp(q)})`, 'gi');
  const parts = text.split(pattern);
  if (parts.length <= 1) {
    return className ? <span className={className}>{text}</span> : text;
  }

  const normalizedQuery = q.toLowerCase();

  return (
    <span className={className}>
      {parts.map((part, index) =>
        part.toLowerCase() === normalizedQuery ? (
          <mark
            key={`${index}-${part}`}
            className={cn(
              'rounded-[2px] bg-primary/20 px-0.5 text-inherit',
              markClassName,
            )}
          >
            {part}
          </mark>
        ) : (
          part
        ),
      )}
    </span>
  );
}
